<script setup>

import { inject, ref, computed, watch } from 'vue';
import { getPositionBriqueRapport } from '@/scripts/global.js';
import { getAppelApiGlisserDeplacer } from '@/scripts/gestionGlisserDeplacer.js';
import { 
	apiModifierValeurs,
	apiModifierTextesBriquesRapport } from '@/services/ServiceModele.js';

import BoutonMenuActions from './BoutonMenuActions.vue';
import TexteSaisieValeur from '@/components/generiques/TexteSaisieValeur.vue';
import PastilleValeur from '@/components/generiques/PastilleValeur.vue';
import PastilleSerieGraphique from '@/components/generiques/PastilleSerieGraphique.vue';

const getChamp = inject('getChamp');
const appelerApiModifModele = inject('appelerApiModifModele');
const selectionnerElement = inject('selectionnerElement');
const basculerSelection = inject('basculerSelection');
const elementsSelectionnes = inject('elementsSelectionnes');
const getElementParent = inject('getElementParent');
const donnerFocusAuRapport = inject('donnerFocusAuRapport');

const { graphique, element, indice } = defineProps({
	graphique: Object,
	element: Object,
	indice: Number
});

const modeEdition = ref(false);
const texteSaisi = ref(element.valeur?.valBrute);
const composantCellule = ref(null);

const estCelluleEntete = computed(() => {
	return ['AxePrincipal', 'SerieGraphique'].includes(element.type);
});

const infobulle = computed(() => {
	switch (element.type) {
		case 'AxePrincipal':
			return 'Libellé de l\'axe';
		case 'SerieGraphique':
			return 'Libellé de la série';
		default:
			if (element.valeur?.valInfo) {
				return `Valeur non formatée : ${element.valeur.valInfo}`;
			} else {
				return null;
			}
	}
});

const typeDonnee = computed(() => {
	const coord = element.valeur?.coord;
	if (coord) {
		return getChamp(coord.idTypeObjet, coord.idChamp).typeDonnee;
	}
});

watch(() => element, (elt) => {
	texteSaisi.value = elt.valeur?.valBrute;
});

watch(() => element.selectionne, (sel) => {
	if (sel) {
		composantCellule.value.scrollIntoView({behaviour: "smooth", block: "nearest", inline: "nearest"});
	}
});

watch(() => element.modeEdition, (edition) => {
	if (edition) {
		delete element.modeEdition;
		activerModeEdition();
	}
});

watch(() => element.selectionElementParent, (sel) => {
	if (sel) {
		const elementEtParentSuperieur = getElementParent(element);
		if (elementEtParentSuperieur != null) {
			selectionnerElement(elementEtParentSuperieur.element, elementEtParentSuperieur.parent);
		}
		delete element.selectionElementParent;
	}
});

function activerModeEdition() {
	if (element?.valeur && 'valBrute' in element.valeur) {
		modeEdition.value = true;
	}
}

function desactiverModeEdition() {
	modeEdition.value = false;
	texteSaisi.value = element.valeur?.valBrute;
	donnerFocusAuRapport();
}

function activerSurvol(element) {
	if (!element.selectionne) {
		element.survol = true;
	}
}

function desactiverSurvol(element) {
	if (element.survol) {
		delete element.survol;
	}
}

function commencerGlisserDeplacer(event) {
	var positionsBriquesDeplacees;
	if (element.selectionne) {
		positionsBriquesDeplacees = elementsSelectionnes.value.map(elt => getPositionBriqueRapport(elt, elt.elementParent)).reverse();
	} else {
		positionsBriquesDeplacees = [getPositionBriqueRapport(element, graphique)];
	}
	const donneesTransfert = {
		type: 'DEPLACEMENT_BRIQUES',
		positionsBriquesDeplacees: positionsBriquesDeplacees
	};
	event.dataTransfer.setData('text/plain', JSON.stringify(donneesTransfert));
	event.dataTransfer.dropEffect = 'move';
}

function accueillirGlisserDeplacer(event) {
	const appelApi = getAppelApiGlisserDeplacer(event, element, graphique);
	if (appelApi) {
		appelerApiModifModele(appelApi.api, appelApi.argument, (reponseOK) => {
			if (reponseOK) {
				selectionnerElement(element, graphique);
			}
		});		
	}
}

function modifierTexte() {
	if (element.type == 'ValeurSerie' && element.valeur != null) {
		if (element.valeur.coord == null)
			return;
		const modifValeur = {
			coord: element.valeur.coord,
			valeur: texteSaisi.value
		};
		appelerApiModifModele(apiModifierValeurs, [modifValeur], (reponseOK) => {
			if (reponseOK) {
				desactiverModeEdition();				
			}
		});
	} else {
		const infosBriqueRapport = {
			positionBrique: getPositionBriqueRapport(element, graphique),
			texte: texteSaisi.value
		};
		appelerApiModifModele(apiModifierTextesBriquesRapport, [infosBriqueRapport], (reponseOK) => {
			if (reponseOK) {
				desactiverModeEdition();				
			}
		});
	}
}

</script>

<template>
	<div class="conteneur-cellule" 
		ref="composantCellule"
		:draggable="element.selectionne && !modeEdition"
		@dragstart.stop="commencerGlisserDeplacer"
		@dragenter.stop.prevent="activerSurvol(element)"
		@dragover.stop.prevent=""
		@dragleave.stop.prevent="desactiverSurvol(element)"
		@drop.stop.prevent="accueillirGlisserDeplacer">
		<div v-if="!modeEdition"
			class="cellule"
			:class="{selectionne: element.selectionne, survol: element.survol, 'cellule-entete': estCelluleEntete, 'aucune-valeur': !element.valeur}"
			v-tooltip="infobulle"
			@mousedown.exact.stop="selectionnerElement(element, graphique)"
			@mousedown.ctrl.exact.stop="basculerSelection(element, graphique)"
			@dblclick="activerModeEdition()"
			@mouseover.stop="activerSurvol(element)"
			@mouseout.stop="desactiverSurvol(element)" >
				{{ element?.valeur?.valFormat }}
				<PastilleValeur v-if="element.valeur?.contexte" :valeur="element.valeur" />
				<PastilleSerieGraphique v-else-if="element.type === 'SerieGraphique' && element.couleurs.length === 1" :couleur="element.couleurs[0]" />
		</div>
		<TexteSaisieValeur v-else
            class="cellule"
            v-model:texte="texteSaisi" 
			:typeDonnee="typeDonnee"
			:auto-focus="true"
			@validation="modifierTexte()"
			@annulation="desactiverModeEdition()" />
		<BoutonMenuActions 
			v-if="element.dernierElementSelectionne && !modeEdition"
			:element="element" />
	</div>
</template>

<style scoped>

.conteneur-cellule {
	display: flex;
	scroll-margin: 10px;
	box-shadow: 0px 0px 2px grey;
	min-height: 20px;
}

.cellule-entete {
	background-color: var(--couleur-entete);
}

</style>